import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { ChevronDown, ChevronUp, GripVertical, Loader2, X } from 'lucide-react';
import { colors } from '../../../lib/colors';
import { isSharedMembershipTemplate, MembershipTemplateSharePolicy } from '../../../features/sharedMembership/types';

/**
 * 수강권 상품 표시 순서 변경 모달
 * - 관리자 목록에 있는 상품 전체(활성·비활성·강사 생성 포함)를 서버가 내려준 순서 그대로 보여준다.
 * - 드래그(마우스) / 방향키(키보드) / ▲▼ 버튼(정밀 이동) 세 가지로 옮길 수 있다.
 * - 저장은 부모가 한다. 여기서는 바뀐 순서의 id 배열만 넘긴다.
 * - components/Schedule/Daily/OrderChangeModal 의 강사 순서 모달과 같은 골격을 쓴다.
 */

export type ReorderPassItem = {
    _id: string;
    name: string;
    isAvailable?: boolean;
    BGColor?: string[];
    totalSession?: number;
    validDays?: number;
    totalPrice?: number;
    issuer?: { issuerRole?: string; name?: string };
    sharePolicy?: MembershipTemplateSharePolicy | null;
    sortOrder?: number;
    [key: string]: any;
};

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(18, 29, 49, 0.48);
    backdrop-filter: blur(2px);
`;

const ModalContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: min(560px, calc(100vw - 32px));
    max-height: min(780px, calc(100vh - 40px));
    overflow: hidden;
    border: 1px solid #e2e8f1;
    border-radius: 18px;
    background: #fff;
    box-shadow: 0 24px 64px rgba(18, 37, 69, 0.24);
`;

const ModalHeader = styled.header`
    display: flex;
    flex: none;
    align-items: flex-start;
    gap: 12px;
    padding: 20px 20px 16px;
    border-bottom: 1px solid #edf1f6;
`;

const HeaderCopy = styled.div`
    min-width: 0;
    flex: 1;

    h2 {
        margin: 0;
        color: #1f2d43;
        font-size: 19px;
        font-weight: 800;
        line-height: 1.35;
        letter-spacing: -0.02em;
    }

    p {
        margin: 5px 0 0;
        color: #718096;
        font-size: 12.5px;
        font-weight: 500;
        line-height: 1.45;
    }
`;

const CountBadge = styled.span`
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    min-width: 42px;
    height: 28px;
    margin-top: 1px;
    padding: 0 10px;
    border-radius: 999px;
    color: #3568ca;
    background: #edf4ff;
    font-size: 12px;
    font-weight: 800;
`;

const CloseButton = styled.button`
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 9px;
    color: #778399;
    background: transparent;
    cursor: pointer;

    &:hover:not(:disabled) {
        color: #34445d;
        background: #f2f5f9;
    }
    &:focus-visible {
        outline: 2px solid #3d73dd;
        outline-offset: 2px;
    }
    &:disabled {
        cursor: default;
        opacity: 0.45;
    }
`;

const List = styled.div`
    min-height: 104px;
    padding: 8px 12px;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;

    &::-webkit-scrollbar {
        width: 8px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        border: 2px solid #fff;
        border-radius: 999px;
        background: #c9d2df;
    }
    &::-webkit-scrollbar-thumb:hover {
        background: #aeb9c8;
    }
`;

const Item = styled.div<{ $isDragging?: boolean; $isAnyDragging?: boolean; $isSaving?: boolean; $inactive?: boolean }>`
    position: relative;
    z-index: ${(props) => (props.$isDragging ? 2 : 1)};
    display: grid;
    grid-template-columns: 34px 14px minmax(0, 1fr) 30px 36px;
    column-gap: 8px;
    align-items: center;
    min-height: 56px;
    margin: 4px 0;
    padding: 6px 6px 6px 8px;
    border: 1px solid ${(props) => (props.$isDragging ? '#7ca5ed' : '#e3e9f1')};
    border-radius: 10px;
    background: ${(props) => (props.$isDragging ? '#edf4ff' : props.$inactive ? '#fafbfc' : '#fff')};
    box-shadow: ${(props) => (props.$isDragging ? '0 8px 20px rgba(48, 92, 172, 0.18)' : 'none')};
    opacity: ${(props) => (props.$isSaving ? 0.68 : 1)};
    transition: border-color 0.16s ease, background 0.16s ease, box-shadow 0.16s ease;

    &:hover {
        border-color: ${(props) => (props.$isAnyDragging || props.$isSaving ? '#e3e9f1' : '#b9ccec')};
    }
`;

const Rank = styled.span`
    color: #8a96a8;
    font-size: 11px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    text-align: center;
`;

const ColorChip = styled.span<{ $from: string; $to: string; $inactive?: boolean }>`
    display: block;
    width: 14px;
    height: 36px;
    border-radius: 4px;
    background: ${(props) =>
        props.$inactive ? colors.GREY_200 : `linear-gradient(135deg, ${props.$from} 0%, ${props.$to} 100%)`};
`;

const Body = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
`;

const NameRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
`;

const Name = styled.span<{ $inactive?: boolean }>`
    min-width: 0;
    overflow: hidden;
    color: ${(props) => (props.$inactive ? '#8a96a8' : '#27364f')};
    font-size: 14px;
    font-weight: 750;
    line-height: 1.35;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Tag = styled.span<{ $tone: 'muted' | 'coach' | 'shared' }>`
    flex: none;
    padding: 1px 6px;
    border-radius: 999px;
    font-size: 10.5px;
    font-weight: 700;
    line-height: 1.5;
    color: ${(props) => (props.$tone === 'muted' ? '#6b7280' : props.$tone === 'coach' ? '#0f766e' : '#7c3aed')};
    background: ${(props) => (props.$tone === 'muted' ? '#eef0f3' : props.$tone === 'coach' ? '#e6f7f4' : '#f3eefe')};
`;

const Meta = styled.span`
    color: #7d899a;
    font-size: 11.5px;
    font-weight: 500;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const StepButtons = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const StepButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 20px;
    padding: 0;
    border: 1px solid #e3e9f1;
    border-radius: 6px;
    color: #7c8aa0;
    background: #fff;
    cursor: pointer;

    &:hover:not(:disabled) {
        color: #3568ca;
        border-color: #b9ccec;
        background: #edf4ff;
    }
    &:focus-visible {
        outline: 2px solid #3d73dd;
        outline-offset: 1px;
    }
    &:disabled {
        color: #c9d2df;
        cursor: default;
    }
`;

const DragHandle = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 34px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    color: #7c8aa0;
    background: transparent;
    touch-action: none;
    cursor: grab;

    &:hover:not(:disabled) {
        color: #3568ca;
        background: #edf4ff;
    }
    &:active:not(:disabled) {
        cursor: grabbing;
    }
    &:focus-visible {
        outline: 2px solid #3d73dd;
        outline-offset: 1px;
    }
    &:disabled {
        cursor: default;
    }
`;

const EmptyState = styled.div`
    display: flex;
    min-height: 120px;
    align-items: center;
    justify-content: center;
    color: #7c899c;
    font-size: 13px;
`;

const ModalFooter = styled.footer`
    display: flex;
    flex: none;
    align-items: center;
    gap: 14px;
    padding: 14px 18px 16px;
    border-top: 1px solid #edf1f6;
    background: #fff;
`;

const FooterNote = styled.span<{ $changed: boolean }>`
    min-width: 0;
    flex: 1;
    color: ${(props) => (props.$changed ? '#3568ca' : '#7d899a')};
    font-size: 11.5px;
    font-weight: ${(props) => (props.$changed ? 700 : 500)};
    line-height: 1.4;
`;

const ButtonGroup = styled.div`
    display: inline-flex;
    flex: none;
    align-items: center;
    gap: 8px;
`;

const Button = styled.button<{ $variant: 'cancel' | 'apply' }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: ${(props) => (props.$variant === 'apply' ? '94px' : '66px')};
    height: 38px;
    padding: 0 14px;
    border: 1px solid ${(props) => (props.$variant === 'apply' ? '#3d73dd' : '#d8e0eb')};
    border-radius: 9px;
    color: ${(props) => (props.$variant === 'apply' ? '#fff' : '#4e5d72')};
    background: ${(props) => (props.$variant === 'apply' ? '#3d73dd' : '#fff')};
    font-size: 12.5px;
    font-weight: 800;
    cursor: pointer;

    &:hover:not(:disabled) {
        border-color: ${(props) => (props.$variant === 'apply' ? '#315fba' : '#bdc8d7')};
        background: ${(props) => (props.$variant === 'apply' ? '#315fba' : '#f7f9fc')};
    }
    &:focus-visible {
        outline: 2px solid #3d73dd;
        outline-offset: 2px;
    }
    &:disabled {
        border-color: #e3e8ef;
        color: #a3adbb;
        background: #f3f5f8;
        cursor: default;
    }
`;

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

const SavingIcon = styled(Loader2)`
    margin-right: 6px;
    animation: ${spin} 0.8s linear infinite;
`;

const formatMeta = (item: ReorderPassItem) => {
    const parts: string[] = [];
    if (typeof item.totalSession === 'number') parts.push(`총 ${item.totalSession}회`);
    if (typeof item.validDays === 'number') parts.push(`${item.validDays}일`);
    if (typeof item.totalPrice === 'number') parts.push(`${item.totalPrice.toLocaleString()}원`);
    return parts.join(' · ');
};

interface SortableRowProps {
    item: ReorderPassItem;
    index: number;
    total: number;
    isAnyDragging: boolean;
    isSaving: boolean;
    onMove: (index: number, direction: -1 | 1) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({ item, index, total, isAnyDragging, isSaving, onMove }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: item._id,
        disabled: isSaving,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition: isDragging ? 'none' : transition,
    };
    const inactive = item.isAvailable === false;
    const isCoach = item.issuer?.issuerRole === 'coach';
    const isShared = isSharedMembershipTemplate(item);
    const [from, to] = Array.isArray(item.BGColor) && item.BGColor.length >= 2 ? item.BGColor : ['#9CA3AF', '#6B7280'];

    return (
        <Item
            ref={setNodeRef}
            style={style}
            $isDragging={isDragging}
            $isAnyDragging={isAnyDragging}
            $isSaving={isSaving}
            $inactive={inactive}
        >
            <Rank aria-hidden="true">{String(index + 1).padStart(2, '0')}</Rank>
            <ColorChip $from={from} $to={to} $inactive={inactive} aria-hidden="true" />
            <Body>
                <NameRow>
                    <Name $inactive={inactive} title={item.name}>
                        {item.name}
                    </Name>
                    {inactive && <Tag $tone="muted">비활성</Tag>}
                    {isCoach && <Tag $tone="coach">강사생성</Tag>}
                    {isShared && <Tag $tone="shared">공유</Tag>}
                </NameRow>
                <Meta>{formatMeta(item)}</Meta>
            </Body>
            <StepButtons>
                <StepButton
                    type="button"
                    aria-label={`${item.name} 한 칸 위로`}
                    disabled={isSaving || index === 0}
                    onClick={() => onMove(index, -1)}
                >
                    <ChevronUp size={14} aria-hidden="true" />
                </StepButton>
                <StepButton
                    type="button"
                    aria-label={`${item.name} 한 칸 아래로`}
                    disabled={isSaving || index === total - 1}
                    onClick={() => onMove(index, 1)}
                >
                    <ChevronDown size={14} aria-hidden="true" />
                </StepButton>
            </StepButtons>
            <DragHandle
                type="button"
                data-reorder-handle=""
                disabled={isSaving}
                aria-label={`${item.name} 순서 이동, 현재 ${index + 1}번째`}
                {...attributes}
                {...listeners}
            >
                <GripVertical size={20} aria-hidden="true" />
            </DragHandle>
        </Item>
    );
};

interface ModalReorderPassProps {
    isOpen: boolean;
    onClose: () => void;
    templates: ReorderPassItem[];
    onApply: (orderedIds: string[]) => void;
    isSaving?: boolean;
}

const ModalReorderPass: React.FC<ModalReorderPassProps> = ({ isOpen, onClose, templates, onApply, isSaving = false }) => {
    // 열리는 순간의 목록을 스냅샷으로 잡는다. 목록 쿼리가 refetchOnWindowFocus:'always' 라
    // 창을 갔다 오면 새 배열이 내려오는데, 그때마다 동기화하면 드래그 중인 순서가 날아간다.
    // 그 사이 생긴 상품은 요청에 빠져도 서버가 건드리지 않고(정렬 전 상태로 맨 위), 삭제된 상품은 404 로 알린다.
    const [snapshot] = useState<ReorderPassItem[]>(templates);
    const [items, setItems] = useState<ReorderPassItem[]>(snapshot);
    const [isAnyDragging, setIsAnyDragging] = useState(false);
    const dialogRef = useRef<HTMLDivElement>(null);
    // dnd-kit 키보드 드래그 중 Escape 는 '드래그 취소'다. 그때 모달까지 닫지 않도록 ref 로 본다(effect 재등록 없이).
    const isDraggingRef = useRef(false);

    const originalOrder = useMemo(() => snapshot.map((item) => item._id), [snapshot]);
    const currentOrder = useMemo(() => items.map((item) => item._id), [items]);
    const hasChanges =
        originalOrder.length === currentOrder.length && originalOrder.some((id, index) => id !== currentOrder[index]);

    const requestClose = useCallback(() => {
        if (isSaving) return;
        if (hasChanges && !window.confirm('변경한 순서를 저장하지 않고 닫을까요?')) return;
        onClose();
    }, [hasChanges, isSaving, onClose]);
    // 아래 포커스 effect 는 열릴 때 한 번만 돌아야 한다(드래그·저장으로 hasChanges/isSaving 이 바뀔 때마다
    // 다시 돌면 포커스가 첫 항목으로 튄다). 최신 requestClose 는 ref 로 읽는다.
    // 렌더 중 ref 대입은 중단·폐기된 렌더의 closure 가 새어 나갈 수 있어 커밋 뒤(useLayoutEffect)에 동기화한다.
    const requestCloseRef = useRef(requestClose);
    useLayoutEffect(() => {
        requestCloseRef.current = requestClose;
    }, [requestClose]);

    // 키보드 접근성: 열리면 포커스를 모달 안으로 옮기고, Tab 을 모달 안에 가두고, 닫히면 원래 자리로 돌려준다.
    // 배경의 「순서 변경」 버튼에 포커스가 남으면 Tab 으로 뒤쪽 필터를 조작할 수 있어 막는다.
    useEffect(() => {
        if (!isOpen) return;
        const dialog = dialogRef.current;
        const previouslyFocused = document.activeElement as HTMLElement | null;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        // dnd-kit 이 disabled 핸들에도 tabIndex=0 을 주므로 disabled/aria-disabled 는 직접 걸러낸다.
        const isFocusable = (element: HTMLElement) =>
            !element.hasAttribute('disabled') && element.getAttribute('aria-disabled') !== 'true';
        const getFocusable = () =>
            Array.from(
                dialog?.querySelectorAll<HTMLElement>(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                ) ?? []
            ).filter(isFocusable);
        // 첫 드래그 핸들(없으면 첫 버튼)로 포커스. 화면 첫 항목부터 방향키로 바로 옮길 수 있다.
        const firstHandle = dialog?.querySelector<HTMLElement>('[data-reorder-handle]');
        ((firstHandle && isFocusable(firstHandle) ? firstHandle : null) ?? getFocusable()[0] ?? dialog)?.focus();

        // 위에 뜬 다른 대화상자(공통 alert 등 role=dialog/alertdialog)에 포커스가 있을 때만 양보한다.
        // 그 밖의 곳(배경)으로 새어 나간 포커스는 Tab 때 다시 안으로 끌어온다.
        const isFocusInOtherDialog = () => {
            const active = document.activeElement as HTMLElement | null;
            if (!active || active === document.body || dialog?.contains(active)) return false;
            const owner = active.closest('[role="dialog"], [role="alertdialog"]');
            return !!owner && owner !== dialog;
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (isFocusInOtherDialog()) return;
            if (event.key === 'Escape') {
                if (isDraggingRef.current) return; // dnd-kit 이 드래그 취소로 처리한다
                event.preventDefault();
                requestCloseRef.current();
                return;
            }
            if (event.key !== 'Tab') return;
            if (isDraggingRef.current) {
                // 키보드로 집은 상태에서 포커스가 다른 버튼으로 가면 Space/Enter 가 그 버튼 대신 '놓기'로 먹힌다
                event.preventDefault();
                return;
            }
            const focusable = getFocusable();
            if (focusable.length === 0) {
                event.preventDefault();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement as HTMLElement | null;
            const outside = !active || active === document.body || !dialog?.contains(active);
            if (event.shiftKey && (active === first || outside)) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (active === last || outside)) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeyDown);
            // 닫힐 때 포커스 복원. 단, 결과 알럿이 이미 포커스를 가져갔으면(alert 가 확인 버튼에 포커스)
            // 빼앗지 않는다. 모달 DOM 이 사라져 포커스가 body 로 떨어진 경우에만 원래 자리로 돌린다.
            const active = document.activeElement;
            if (!active || active === document.body || dialog?.contains(active)) {
                previouslyFocused?.focus?.();
            }
        };
    }, [isOpen]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        isDraggingRef.current = false;
        setIsAnyDragging(false);
        if (!over || active.id === over.id) return;
        setItems((list) => {
            const oldIndex = list.findIndex((item) => item._id === active.id);
            const newIndex = list.findIndex((item) => item._id === over.id);
            if (oldIndex < 0 || newIndex < 0) return list;
            return arrayMove(list, oldIndex, newIndex);
        });
    };

    const handleMove = (index: number, direction: -1 | 1) => {
        setItems((list) => {
            const target = index + direction;
            if (target < 0 || target >= list.length) return list;
            return arrayMove(list, index, target);
        });
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay onMouseDown={(event) => event.target === event.currentTarget && requestClose()}>
            <ModalContainer
                ref={dialogRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-labelledby="pass-order-title"
                aria-describedby="pass-order-description"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <ModalHeader>
                    <HeaderCopy>
                        <h2 id="pass-order-title">수강권 표시 순서</h2>
                        <p id="pass-order-description">
                            드래그하거나 ▲▼ 로 옮기세요. 저장한 순서는 수강권 관리 목록과 회원 발급 화면에 같이 적용돼요.
                        </p>
                    </HeaderCopy>
                    <CountBadge aria-label={`수강권 ${items.length}개`}>{items.length}개</CountBadge>
                    <CloseButton type="button" aria-label="닫기" onClick={requestClose} disabled={isSaving}>
                        <X size={18} aria-hidden="true" />
                    </CloseButton>
                </ModalHeader>

                <List>
                    {items.length === 0 ? (
                        <EmptyState>순서를 정할 수강권이 없어요.</EmptyState>
                    ) : (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
                            onDragStart={() => {
                                isDraggingRef.current = true;
                                setIsAnyDragging(true);
                            }}
                            onDragCancel={() => {
                                isDraggingRef.current = false;
                                setIsAnyDragging(false);
                            }}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext items={currentOrder} strategy={verticalListSortingStrategy}>
                                {items.map((item, index) => (
                                    <SortableRow
                                        key={item._id}
                                        item={item}
                                        index={index}
                                        total={items.length}
                                        isAnyDragging={isAnyDragging}
                                        isSaving={isSaving}
                                        onMove={handleMove}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    )}
                </List>

                <ModalFooter>
                    <FooterNote $changed={hasChanges}>
                        {hasChanges
                            ? '순서가 바뀌었어요. 저장을 눌러야 적용돼요.'
                            : '새로 만든 수강권은 목록 맨 위에 와요. 여기서 원하는 자리로 옮기세요.'}
                    </FooterNote>
                    <ButtonGroup>
                        <Button type="button" $variant="cancel" onClick={requestClose} disabled={isSaving}>
                            취소
                        </Button>
                        <Button
                            type="button"
                            $variant="apply"
                            onClick={() => onApply(currentOrder)}
                            disabled={isSaving || !hasChanges}
                        >
                            {isSaving && <SavingIcon size={14} aria-hidden="true" />}
                            {isSaving ? '저장 중' : '순서 저장'}
                        </Button>
                    </ButtonGroup>
                </ModalFooter>
            </ModalContainer>
        </ModalOverlay>
    );
};

export default ModalReorderPass;
